/**
 * @file SubmissionReadinessPanel.jsx
 * @module ui/src/components/tenant-admin/SubmissionReadinessPanel
 * @description
 *   Renders the complete submit-time readiness state for a compensation plan while
 *   keeping submission validation categorically separate from approver decision context.
 *   It also surfaces approval-routing failure before a governance submission occurs.
 *
 * @version 1.0.1
 * @since S755
 * @author data-fit SME Build · Reviewed: Claude S755
 *
 * @architecture
 *   Locked decisions : #14
 *   Temporal invariants : INV-3
 *   Design principles : DP-10, DP-14
 *   Cross-cutting contracts : XC-4
 *
 * @domain-rules
 *   - SAVE and SUBMIT are permanently distinct operations; this component represents SUBMIT-time validation only.
 *   - Server-computed canSubmit is authoritative and must never be reconstructed from client-side issue counts.
 *   - Submission validation and approver decision context are different governance concepts and must remain visually separate.
 *   - A missing eligible approver is a blocking production condition and must be visible before submission.
 *   - Data freshness signals inform an approver but do not become submission blockers merely because they are stale.
 *   - Plan workflow status does not establish temporal applicability.
 *
 * @session-6-migration-flags
 *   - none
 *
 * @changelog
 *   1.0.1  S755  Corrected ValidationGroup navigation contract — OI-S755-C19-NAVIGATE-PATTERN-01
 *   1.0.0  S755  Initial build — Group D batch delivery
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import DataFreshnessIndicator from '../shared/DataFreshnessIndicator';

/**
 * @typedef {Object} ValidationItem
 * @property {string} id
 * @property {'blocking'|'warning'|'passed'} severity
 * @property {string} message
 * @property {string} [sectionKey]
 * @property {string} [fieldKey]
 */

/**
 * @typedef {Object} ValidationResult
 * @property {boolean} canSubmit
 * @property {ValidationItem[]} blocking
 * @property {ValidationItem[]} warnings
 * @property {ValidationItem[]} passed
 * @property {boolean} noEligibleApprover
 * @property {string} [noEligibleApproverDetail]
 */

/**
 * @typedef {Object} FreshnessSignal
 * @property {'quota'|'credits'|'population'|'projection'} signal
 * @property {'current'|'stale'|'warning'} status
 * @property {string} freshnessAt
 * @property {string} label
 */

/**
 * @typedef {Object} DecisionContext
 * @property {FreshnessSignal[]} freshnessSignals
 * @property {string} populationCount
 * @property {string} [projectionNote]
 */

/**
 * @typedef {Object} SubmissionReadinessPanelProps
 * @property {ValidationResult} validationResult
 * @property {DecisionContext} decisionContext
 * @property {boolean} submitting
 * @property {(sectionKey: string) => void} onNavigateToSection
 * @property {() => void} onSubmit
 * @property {() => void} onClose
 */

const styles = {
  panel: {
    fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
    fontSize: 'var(--font-sz-base)',
    color: 'rgb(var(--color-text))',
    background: 'rgb(var(--color-surface))',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '20px',
    borderBottom: '1px solid rgb(var(--color-border))',
  },
  heading: {
    margin: 0,
    fontSize: '20px',
    lineHeight: 1.3,
  },
  muted: {
    margin: '6px 0 0',
    color: 'rgb(var(--color-text-muted))',
  },
  section: {
    padding: '20px',
  },
  decisionSection: {
    padding: '20px',
    borderTop: '2px solid rgb(var(--color-border))',
    background: 'rgb(var(--color-surface-muted))',
  },
  sectionHeading: {
    margin: 0,
    fontSize: '16px',
  },
  disclosureButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '10px 0',
    border: 0,
    background: 'transparent',
    color: 'rgb(var(--color-text))',
    font: 'inherit',
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'left',
  },
  issueList: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 14px',
    display: 'grid',
    gap: '8px',
  },
  issue: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '12px',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '6px',
  },
  blockingIssue: {
    borderLeft: '4px solid rgb(var(--color-danger))',
  },
  warningIssue: {
    borderLeft: '4px solid rgb(var(--color-warning))',
  },
  passedIssue: {
    borderLeft: '4px solid rgb(var(--color-success))',
  },
  issueBody: {
    minWidth: 0,
  },
  severityText: {
    margin: '0 0 4px',
    fontWeight: 600,
  },
  issueMessage: {
    margin: 0,
  },
  detail: {
    margin: '5px 0 0',
    color: 'rgb(var(--color-text-muted))',
  },
  empty: {
    margin: '0 0 12px',
    color: 'rgb(var(--color-text-muted))',
  },
  contextRows: {
    display: 'grid',
    gap: '10px',
    marginTop: '14px',
  },
  contextRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(140px, 1fr) minmax(180px, 1.5fr)',
    gap: '16px',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid rgb(var(--color-border))',
  },
  contextLabel: {
    fontWeight: 600,
  },
  population: {
    margin: '14px 0 0',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    padding: '16px 20px',
    borderTop: '1px solid rgb(var(--color-border))',
  },
};

function ValidationGroup({
  title,
  items,
  expanded,
  onToggle,
  onNavigate,
  severity,
  emptyMessage,
}) {
  // WHY: Each validation severity has its own disclosure state so confidence-building
  // passed checks can remain secondary while blockers and warnings stay prominent.
  const issueStyle =
    severity === 'blocking'
      ? styles.blockingIssue
      : severity === 'warning'
        ? styles.warningIssue
        : styles.passedIssue;

  return (
    <div>
      <button
        type="button"
        style={styles.disclosureButton}
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <span>{title}</span>
        <span aria-hidden="true">{expanded ? '−' : '+'}</span>
      </button>

      {/* WHY: Collapsed groups remain represented by their heading and count without
          forcing lower-priority information into the admin's primary scan path. */}
      {expanded ? (
        items.length > 0 ? (
          <ul style={styles.issueList}>
            {items.map((item) => (
              <li
                key={item.id}
                style={{ ...styles.issue, ...issueStyle }}
              >
                <div style={styles.issueBody}>
                  <p style={styles.severityText}>
                    {severity === 'blocking'
                      ? 'Blocking'
                      : severity === 'warning'
                        ? 'Warning'
                        : 'Passed'}
                  </p>

                  <p style={styles.issueMessage}>{item.message}</p>

                  {/* WHY: Optional detail is rendered only when supplied by the authoritative
                      validation result; the component does not fabricate remediation context. */}
                  {item.detail ? (
                    <p style={styles.detail}>{item.detail}</p>
                  ) : null}
                </div>

                {/* WHY: Deep links are rendered only when the server supplied a
                    responsible section; navigation is passed explicitly rather than
                    attached as a mutable property on the disclosure callback. */}
                {item.sectionKey ? (
                  <button
                    type="button"
                    className="text-button"
                    onClick={() => onNavigate(item.sectionKey)}
                  >
                    Go to {item.sectionKey}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p style={styles.empty}>{emptyMessage}</p>
        )
      ) : null}
    </div>
  );
}

ValidationGroup.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      severity: PropTypes.oneOf(['blocking', 'warning', 'passed']).isRequired,
      message: PropTypes.string.isRequired,
      sectionKey: PropTypes.string,
      fieldKey: PropTypes.string,
      detail: PropTypes.string,
    }),
  ).isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  severity: PropTypes.oneOf(['blocking', 'warning', 'passed']).isRequired,
  emptyMessage: PropTypes.string.isRequired,
};

/**
 * @param {SubmissionReadinessPanelProps} props
 * @returns {React.ReactElement}
 */
export default function SubmissionReadinessPanel({
  validationResult,
  decisionContext,
  submitting,
  onNavigateToSection,
  onSubmit,
  onClose,
}) {
  // WHY: Blocking checks start expanded because they directly prevent the governance mutation.
  const [blockingExpanded, setBlockingExpanded] = useState(true);

  // WHY: Warnings start expanded because they are relevant to the submit decision even though
  // the server has classified them as non-blocking.
  const [warningsExpanded, setWarningsExpanded] = useState(true);

  // WHY: Passed checks start collapsed because they build confidence but are not the admin's
  // primary remediation workload.
  const [passedExpanded, setPassedExpanded] = useState(false);

  // WHY: Decision context starts expanded because freshness and population context are
  // governance-critical information for the approver despite not being validation blockers.
  const [contextExpanded, setContextExpanded] = useState(true);

  // WHY: The missing-approver condition must occupy the first blocking position and retain
  // the exact locked copy rather than being buried among ordinary validation issues.
  const blockingItems = validationResult.noEligibleApprover
    ? [
        {
          id: '__no-eligible-approver__',
          severity: 'blocking',
          message: "No eligible approver exists for this plan's scope.",
          detail: validationResult.noEligibleApproverDetail,
        },
        ...validationResult.blocking,
      ]
    : validationResult.blocking;

  // WHY: Navigation remains a parent-owned document concern; the panel only requests
  // navigation to the server-identified responsible section.
  const handleNavigate = (sectionKey) => {
    onNavigateToSection(sectionKey);
  };

  // WHY: Submit must remain a parent-owned governance mutation. This component performs
  // no optimistic state transition before or after the callback.
  const handleSubmit = () => {
    onSubmit();
  };

  // WHY: Dismissal is explicit. Resolution of blockers enables submit but never silently
  // dismisses a panel the admin may still need for warnings or decision context.
  const handleClose = () => {
    onClose();
  };

  // WHY: Blocking disclosure changes presentation only and never alters server validation state.
  const toggleBlocking = () => {
    setBlockingExpanded((current) => !current);
  };

  // WHY: Warning disclosure is independent so admins may reduce visual density without
  // changing the server-defined warning classification.
  const toggleWarnings = () => {
    setWarningsExpanded((current) => !current);
  };

  // WHY: Passed checks are optional confidence detail and therefore independently collapsible.
  const togglePassed = () => {
    setPassedExpanded((current) => !current);
  };

  // WHY: Decision context remains a separate disclosure region so its freshness signals
  // cannot be visually mistaken for blocking validation severity.
  const toggleContext = () => {
    setContextExpanded((current) => !current);
  };

  return (
    <aside style={styles.panel} aria-labelledby="submission-readiness-title">
      <div style={styles.header}>
        <div>
          <h2 id="submission-readiness-title" style={styles.heading}>
            Submission readiness
          </h2>
          <p style={styles.muted}>
            Review submit-time validation and approver decision context before
            sending this plan into governance.
          </p>
        </div>

        <button
          type="button"
          className="icon-button"
          aria-label="Close submission readiness panel"
          onClick={handleClose}
        >
          ×
        </button>
      </div>

      {/* WHY: Submission validation is permanently rendered first because it answers whether
          governance submission is structurally and semantically allowed. */}
      <section
        style={styles.section}
        aria-labelledby="submission-validation-heading"
      >
        <h3
          id="submission-validation-heading"
          style={styles.sectionHeading}
        >
          Submission validation
        </h3>

        <p style={styles.muted}>
          Blocking issues prevent submission. Warnings do not prevent submission.
        </p>

        <ValidationGroup
          title={`Blocking (${blockingItems.length})`}
          items={blockingItems}
          expanded={blockingExpanded}
          onToggle={toggleBlocking}
          onNavigate={handleNavigate}
          severity="blocking"
          emptyMessage="No blocking submission issues."
        />

        <ValidationGroup
          title={`Warnings (${validationResult.warnings.length})`}
          items={validationResult.warnings}
          expanded={warningsExpanded}
          onToggle={toggleWarnings}
          onNavigate={handleNavigate}
          severity="warning"
          emptyMessage="No submission warnings."
        />

        <ValidationGroup
          title={`Passed (${validationResult.passed.length})`}
          items={validationResult.passed}
          expanded={passedExpanded}
          onToggle={togglePassed}
          onNavigate={handleNavigate}
          severity="passed"
          emptyMessage="No explicit passed checks were returned."
        />
      </section>

      {/* WHY: Decision context is rendered in a physically separate section because XC-4
          prohibits freshness signals from sharing blocker/warning severity semantics. */}
      <section
        style={styles.decisionSection}
        aria-labelledby="approver-context-heading"
      >
        <button
          type="button"
          style={styles.disclosureButton}
          aria-expanded={contextExpanded}
          onClick={toggleContext}
        >
          <span id="approver-context-heading">
            Data context for approver
          </span>
          <span aria-hidden="true">{contextExpanded ? '−' : '+'}</span>
        </button>

        {/* WHY: Freshness is informational governance context. It is intentionally rendered
            without blocking/warning submission severity badges. */}
        {contextExpanded ? (
          <>
            <div style={styles.contextRows}>
              {decisionContext.freshnessSignals.map((freshnessSignal) => (
                <div
                  key={freshnessSignal.signal}
                  style={styles.contextRow}
                >
                  <span style={styles.contextLabel}>
                    {freshnessSignal.label}
                  </span>

                  <DataFreshnessIndicator
                    signal={freshnessSignal.signal}
                    status={freshnessSignal.status}
                    freshnessAt={freshnessSignal.freshnessAt}
                  />
                </div>
              ))}
            </div>

            <p style={styles.population}>
              <strong>Matching population:</strong>{' '}
              {decisionContext.populationCount}
            </p>

            {/* WHY: Projection coverage is rendered only when the server supplied explanatory
                context; the UI does not infer or calculate projection quality. */}
            {decisionContext.projectionNote ? (
              <p className="modifier-manager-info">
                {decisionContext.projectionNote}
              </p>
            ) : null}
          </>
        ) : null}
      </section>

      <div style={styles.actions}>
        <button
          type="button"
          className="secondary-button"
          onClick={handleClose}
        >
          Close
        </button>

        <button
          type="button"
          className="primary-button"
          disabled={!validationResult.canSubmit || submitting}
          onClick={handleSubmit}
        >
          {/* WHY: Pending copy exposes the in-flight governance mutation without assuming success. */}
          {submitting ? 'Submitting…' : 'Submit for approval'}
        </button>
      </div>
    </aside>
  );
}

SubmissionReadinessPanel.propTypes = {
  validationResult: PropTypes.shape({
    canSubmit: PropTypes.bool.isRequired,
    blocking: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        severity: PropTypes.oneOf(['blocking', 'warning', 'passed']).isRequired,
        message: PropTypes.string.isRequired,
        sectionKey: PropTypes.string,
        fieldKey: PropTypes.string,
      }),
    ).isRequired,
    warnings: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        severity: PropTypes.oneOf(['blocking', 'warning', 'passed']).isRequired,
        message: PropTypes.string.isRequired,
        sectionKey: PropTypes.string,
        fieldKey: PropTypes.string,
      }),
    ).isRequired,
    passed: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        severity: PropTypes.oneOf(['blocking', 'warning', 'passed']).isRequired,
        message: PropTypes.string.isRequired,
        sectionKey: PropTypes.string,
        fieldKey: PropTypes.string,
      }),
    ).isRequired,
    noEligibleApprover: PropTypes.bool.isRequired,
    noEligibleApproverDetail: PropTypes.string,
  }).isRequired,
  decisionContext: PropTypes.shape({
    freshnessSignals: PropTypes.arrayOf(
      PropTypes.shape({
        signal: PropTypes.oneOf([
          'quota',
          'credits',
          'population',
          'projection',
        ]).isRequired,
        status: PropTypes.oneOf(['current', 'stale', 'warning']).isRequired,
        freshnessAt: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      }),
    ).isRequired,
    populationCount: PropTypes.string.isRequired,
    projectionNote: PropTypes.string,
  }).isRequired,
  submitting: PropTypes.bool.isRequired,
  onNavigateToSection: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

/**
 * @example
 * <SubmissionReadinessPanel
 *   validationResult={{
 *     canSubmit: false,
 *     blocking: [{
 *       id: 'rate-table',
 *       severity: 'blocking',
 *       message: 'Rate table coverage is incomplete.',
 *       sectionKey: 'measurement-economics',
 *     }],
 *     warnings: [],
 *     passed: [],
 *     noEligibleApprover: false,
 *   }}
 *   decisionContext={{
 *     freshnessSignals: [{
 *       signal: 'quota',
 *       status: 'current',
 *       freshnessAt: '2026-08-12T13:00:00Z',
 *       label: 'Quota data',
 *     }],
 *     populationCount: '842 positions',
 *   }}
 *   submitting={false}
 *   onNavigateToSection={(sectionKey) => navigateToSection(sectionKey)}
 *   onSubmit={() => submitPlan()}
 *   onClose={() => setReadinessOpen(false)}
 * />
 */

/**
 * DOMAIN COMPLIANCE CHECK
 * - SAVE and SUBMIT remain distinct; this component exposes submit-time state only.
 * - validationResult.canSubmit is trusted directly from the server.
 * - Missing eligible approver is injected at the top of the blocking presentation.
 * - Submission validation and decision context have distinct sections and semantics.
 * - DataFreshnessIndicator is used for every supplied freshness signal.
 * - Deep-link navigation is passed through an explicit onNavigate prop.
 * - No mutable function-property navigation pattern is used.
 * - No optimistic submission mutation occurs.
 * - Plan status is not interpreted as temporal applicability.
 */

/**
 * SESSION 6 MIGRATION FLAGS
 * - none
 */

/**
 * ACCESSIBILITY NOTES
 * - Panel and both conceptual sections have explicit accessible headings.
 * - Disclosure controls expose aria-expanded.
 * - State is conveyed with text labels, not color alone.
 * - Close control has an explicit accessible name.
 * - Deep-link buttons are native keyboard-operable controls.
 * - Disabled submit state is native button state and therefore announced by assistive technology.
 */